import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  FileText,
  Download,
  CheckCircle2,
  MapPin,
  Award,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CAPABILITY_STATEMENT } from "../data/capability_statement";
import Logomark from "../components/Logomark";
import { usePresentation } from "../components/PresentationModal";

const easeOut = [0.16, 1, 0.3, 1];

export default function Capabilities() {
  const { lang, toggleLang } = useLang();
  const c = CAPABILITY_STATEMENT[lang];
  const { openModal } = usePresentation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDownload = () => openModal();

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F5]">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0B]/85 border-b border-white/5 print:hidden">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="cap-home">
            <Logomark size={36} />
            <div className="hidden sm:block leading-none text-left border-l border-white/10 pl-3">
              <div className="font-display font-black tracking-tight text-[15px] text-white">
                DEPP C&amp;M
              </div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-[#8B939C] mt-1">
                S.A. DE C.V.
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleLang}
              data-testid="cap-lang-toggle"
              className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#F4F4F5] border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
            >
              {lang.toUpperCase()}
            </button>
            <button
              onClick={handleDownload}
              data-testid="cap-print"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-black bg-[#D4AF37] hover:bg-[#F0C84B] transition-all duration-300"
            >
              <Download className="w-3.5 h-3.5" />
              PDF
            </button>
            <Link
              to="/"
              data-testid="cap-back"
              className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:text-[#D4AF37] transition-colors duration-300"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {c.hero.cta_back}
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-[72px]">
        {/* HERO */}
        <section className="relative py-20 sm:py-28 border-b border-white/5 overflow-hidden">
          <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-[radial-gradient(circle,rgba(212,175,55,0.06),transparent_70%)] pointer-events-none" />

          <div className="relative max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{c.hero.eyebrow}</span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: easeOut }}
              className="font-display font-black text-white text-[clamp(2.25rem,5vw,4.25rem)] tracking-tight leading-[1.02] max-w-[22ch]"
            >
              {c.hero.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: easeOut, delay: 0.15 }}
              className="mt-7 max-w-3xl text-base lg:text-lg text-[#A1A1AA] leading-relaxed"
            >
              {c.hero.subtitle}
            </motion.p>

            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
              {c.hero.meta.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.3 + i * 0.08 }}
                  data-testid={`cap-meta-${i}`}
                  className="bg-[#0F0F11] px-6 lg:px-8 py-8"
                >
                  <div className="font-display font-black text-4xl lg:text-5xl gold-gradient-text tabular-nums tracking-tight">
                    {m.v}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mt-3">
                    {m.k}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={handleDownload}
                data-testid="cap-hero-download"
                className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-[12px] font-bold uppercase tracking-[0.25em] hover:bg-[#F0C84B] transition-colors duration-300"
              >
                <Download className="w-4 h-4" />
                {lang === "es" ? "Descargar Capability Statement" : "Download Capability Statement"}
              </button>
              <Link
                to="/#contact"
                data-testid="cap-cta-contact"
                className="group inline-flex items-center gap-3 border border-white/20 text-white px-7 py-4 text-[12px] font-bold uppercase tracking-[0.25em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
              >
                {c.hero.cta_contact}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* EXECUTIVE SUMMARY */}
        <Section id="exec" eyebrow={c.exec.eyebrow} title={c.exec.title}>
          <div className="space-y-5 text-[15px] leading-[1.75] text-[#A1A1AA] max-w-4xl">
            {c.exec.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </Section>

        {/* CAPABILITIES */}
        <Section id="capabilities" eyebrow={c.capabilities.eyebrow} title={c.capabilities.title}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {c.capabilities.groups.map((g, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.04, ease: easeOut }}
                data-testid={`cap-group-${i}`}
                className="bg-[#0F0F11] p-7 lg:p-8"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 border border-[#D4AF37]/40 flex items-center justify-center">
                    <span className="text-[#D4AF37] text-xs font-display font-bold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </div>
                <h3 className="font-display font-bold text-white text-lg tracking-tight leading-tight mb-4">
                  {g.name}
                </h3>
                <ul className="space-y-2">
                  {g.items.map((it, j) => (
                    <li
                      key={j}
                      className="flex gap-2 text-[13px] leading-relaxed text-[#A1A1AA]"
                    >
                      <span className="text-[#D4AF37] flex-shrink-0 mt-1">—</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* PORTFOLIO */}
        <Section
          id="portfolio"
          eyebrow={c.portfolio.eyebrow}
          title={c.portfolio.title}
          subtitle={c.portfolio.subtitle}
        >
          <div className="space-y-12">
            {c.portfolio.sectors.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: easeOut }}
                data-testid={`cap-sector-${i}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 pb-10 border-b border-white/5 last:border-0"
              >
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[#D4AF37] font-mono text-xs">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="h-px w-8 bg-[#D4AF37]/40" />
                  </div>
                  <h3 className="font-display font-bold text-white text-xl lg:text-2xl tracking-tight leading-tight">
                    {s.name}
                  </h3>
                  <div className="mt-4 text-[11px] uppercase tracking-[0.25em] text-[#8B939C]">
                    {String(s.projects.length).padStart(2, "0")} {lang === "es" ? "proyectos" : "projects"}
                  </div>
                </div>
                <ul className="lg:col-span-8 space-y-3">
                  {s.projects.map((p, j) => (
                    <li
                      key={j}
                      className="flex gap-3 text-[14px] leading-relaxed text-[#D1D1D4] border-l border-[#D4AF37]/20 hover:border-[#D4AF37]/60 pl-4 py-1 transition-colors duration-300"
                    >
                      <CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* ACHIEVEMENTS */}
        <Section id="achievements" eyebrow={c.achievements.eyebrow} title={c.achievements.title}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {c.achievements.items.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.04, ease: easeOut }}
                data-testid={`cap-achievement-${i}`}
                className="bg-[#0F0F11] p-7 lg:p-8 flex gap-4"
              >
                <Award className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-display font-bold text-white text-base lg:text-lg leading-tight">
                    {a.k}
                  </div>
                  <div className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                    {a.v}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* GEOGRAPHICAL PRESENCE */}
        <Section id="presence" eyebrow={c.presence.eyebrow} title={c.presence.title}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {c.presence.items.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                data-testid={`cap-presence-${i}`}
                className="bg-[#0F0F11] p-6 lg:p-7 flex items-start gap-4"
              >
                <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-display font-bold text-white tracking-tight">
                    {p.k}
                  </div>
                  <div className="mt-1 text-sm text-[#8B939C] leading-relaxed">
                    {p.v}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* CLIENTS */}
        <Section id="clients" eyebrow={c.clients.eyebrow} title={c.clients.title}>
          <div className="space-y-10">
            {c.clients.groups.map((g, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: easeOut }}
                data-testid={`cap-client-group-${i}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 border-b border-white/5 pb-8 last:border-0"
              >
                <h3 className="lg:col-span-4 font-display font-bold text-white text-lg tracking-tight">
                  {g.name}
                </h3>
                <div className="lg:col-span-8 flex flex-wrap gap-2">
                  {g.list.map((name, j) => (
                    <span
                      key={j}
                      className="inline-flex items-center gap-2 px-3 py-2 text-[12px] uppercase tracking-[0.15em] text-[#D1D1D4] border border-white/10 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors duration-300"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* DIFFERENTIATORS */}
        <Section id="differentiators" eyebrow={c.differentiators.eyebrow} title={c.differentiators.title}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {c.differentiators.items.map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.04, ease: easeOut }}
                data-testid={`cap-diff-${i}`}
                className="bg-[#0F0F11] p-8 lg:p-10"
              >
                <div className="font-display font-bold text-white text-lg lg:text-xl leading-tight max-w-[22ch]">
                  {d.k}
                </div>
                <div className="mt-4 text-sm text-[#A1A1AA] leading-relaxed max-w-xl">
                  {d.v}
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* CLOSING */}
        <section className="relative py-24 border-t border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-5">
                <FileText className="w-4 h-4 text-[#D4AF37]" />
                <span className="overline">{c.footer.request}</span>
              </div>
              <h3 className="font-display font-bold text-white text-[clamp(1.75rem,3vw,2.75rem)] tracking-tight leading-tight">
                {c.footer.request}
              </h3>
              <p className="mt-6 text-[13px] leading-relaxed text-[#8B939C] max-w-2xl">
                {c.footer.note}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/#contact"
                  data-testid="cap-closing-cta"
                  className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-[12px] font-bold uppercase tracking-[0.25em] hover:bg-[#F0C84B] transition-colors duration-300"
                >
                  {c.hero.cta_contact}
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <button
                  onClick={handleDownload}
                  className="group inline-flex items-center gap-3 border border-white/20 text-white px-7 py-4 text-[12px] font-bold uppercase tracking-[0.25em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
                >
                  <Download className="w-4 h-4" />
                  {lang === "es" ? "Descargar PDF" : "Download PDF"}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-10 print:hidden">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8B939C]">
            © {new Date().getFullYear()} DEPP C&amp;M, S.A. DE C.V.
          </div>
          <Link
            to="/privacidad"
            className="text-[11px] uppercase tracking-[0.2em] text-[#8B939C] hover:text-[#D4AF37]"
          >
            {lang === "es" ? "Aviso de Privacidad" : "Privacy Notice"}
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ id, eyebrow, title, subtitle, children }) {
  return (
    <section id={id} data-testid={`cap-section-${id}`} className="py-20 lg:py-28 border-b border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="lg:max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{eyebrow}</span>
            </div>
            <h2 className="font-display font-bold text-white text-[clamp(1.75rem,3.5vw,3rem)] tracking-tight leading-[1.05] max-w-[22ch]">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-5 text-[15px] leading-relaxed text-[#A1A1AA] max-w-3xl">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}
